import { CandidateEditProfileEmpty } from 'candidate/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import debounce from 'lodash/debounce';
import { v4 as uuidv4 } from 'uuid';

import { AutocompleteV2ViewModel } from 'shared/components/autocomplete-v2';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';

type EducationOption = {
  id: number | string;
  name: string;
};

type EducationSearchResponse = ApiResponseDto<EducationOption[]>;

export type EducationMonth = {
  id: number | string;
  name: string;
};

export type EducationLevel = {
  value: number | string;
  name: string;
};

type EducationFieldValues = {
  eduType: string;
  institution: string;
  faculty: string;
  specialty: string;
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
  stillLearning: boolean;
};

type AdditionalEducationFieldValues = {
  courseName: string;
  institution: string;
  documentName: string;
  courseLink: string;
  certificate: string;
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
  stillLearning: boolean;
};

type EducationInitialValues = Partial<EducationFieldValues> & {
  id?: string;
  institutionId?: number | string | null;
};

type AdditionalEducationInitialValues = Partial<AdditionalEducationFieldValues> & {
  id?: string;
};

function toOption(label: string, id: number | string = `custom:${label}`): EducationOption {
  return {
    id,
    name: label,
  };
}

function withCustomOption(options: EducationOption[], query: string): EducationOption[] {
  const trimmedQuery = query.trim();

  if (
    trimmedQuery.length >= 2 &&
    !options.some((option) => option.name.toLowerCase() === trimmedQuery.toLowerCase())
  ) {
    return [toOption(trimmedQuery), ...options];
  }

  return options;
}

function normalizeCertificate(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  const certificate = value.trim();

  if (!certificate || certificate === 'null' || certificate === 'undefined') {
    return '';
  }

  return certificate;
}

export class EducationItem {
  readonly id: string;
  eduType = '';
  institution = '';
  institutionId: number | string | null = null;
  faculty = '';
  specialty = '';
  fromMonth = '';
  fromYear = '';
  toMonth = '';
  toYear = '';
  stillLearning = false;

  readonly institutionOptions = observable.array<EducationOption>([]);
  readonly facultyOptions = observable.array<EducationOption>([]);
  readonly specialtyOptions = observable.array<EducationOption>([]);
  readonly institutionVM = new AutocompleteV2ViewModel<EducationOption>();
  readonly facultyVM = new AutocompleteV2ViewModel<EducationOption>();
  readonly specialtyVM = new AutocompleteV2ViewModel<EducationOption>();
  readonly institutionSearchState = new AsyncState<EducationSearchResponse>();
  readonly facultiesLoadState = new AsyncState<EducationSearchResponse>();
  readonly specialtySearchState = new AsyncState<EducationSearchResponse>();

  private institutionSearchRequestId = 0;
  private specialtySearchRequestId = 0;
  private activeInstitutionSearchRequestId: number | null = null;
  private activeSpecialtySearchRequestId: number | null = null;
  private readonly debouncedFetchInstitutions = debounce((query: string) => {
    this.getInstitutions(query);
  }, 500);
  private readonly debouncedFetchSpecialties = debounce((query: string) => {
    this.getSpecialties(query);
  }, 500);

  constructor(initialValues: EducationInitialValues = {}) {
    this.id = initialValues.id ?? uuidv4();
    this.eduType = initialValues.eduType ?? '';
    this.institution = initialValues.institution ?? '';
    this.institutionId = initialValues.institutionId ?? null;
    this.faculty = initialValues.faculty ?? '';
    this.specialty = initialValues.specialty ?? '';
    this.fromMonth = initialValues.fromMonth ?? '';
    this.fromYear = initialValues.fromYear ?? '';
    this.toMonth = initialValues.toMonth ?? '';
    this.toYear = initialValues.toYear ?? '';
    this.stillLearning = initialValues.stillLearning ?? false;

    makeObservable(this, {
      eduType: observable,
      institution: observable,
      institutionId: observable,
      faculty: observable,
      specialty: observable,
      fromMonth: observable,
      fromYear: observable,
      toMonth: observable,
      toYear: observable,
      stillLearning: observable,
      institutionOptions: observable,
      facultyOptions: observable,
      specialtyOptions: observable,
      valid: computed,
      showDetails: computed,
      institutionAutocompleteOptions: computed,
      facultyAutocompleteOptions: computed,
      specialtyAutocompleteOptions: computed,
      isInstitutionLoading: computed,
      isFacultiesLoading: computed,
      isSpecialtyLoading: computed,
      onSearchInstitution: action.bound,
      onSelectInstitution: action.bound,
      onSearchFaculty: action.bound,
      onSelectFaculty: action.bound,
      onSearchSpecialty: action.bound,
      onSelectSpecialty: action.bound,
      updateField: action.bound,
      setFacultyOptions: action.bound,
      dispose: action.bound,
    });

    this.institutionVM.setInputValue(this.institution);
    this.facultyVM.setInputValue(this.faculty);
    this.specialtyVM.setInputValue(this.specialty);
  }

  get valid(): boolean {
    const hasEndDate =
      this.stillLearning || Boolean(this.toMonth.trim() && this.toYear.trim());

    return Boolean(
      this.eduType.trim() &&
        this.institution.trim() &&
        this.fromMonth.trim() &&
        this.fromYear.trim() &&
        hasEndDate
    );
  }

  get showDetails(): boolean {
    return this.eduType !== '7';
  }

  get institutionAutocompleteOptions(): EducationOption[] {
    return withCustomOption(this.institutionOptions.slice(), this.institutionVM.inputValue);
  }

  get facultyAutocompleteOptions(): EducationOption[] {
    return withCustomOption(this.facultyOptions.slice(), this.facultyVM.inputValue);
  }

  get specialtyAutocompleteOptions(): EducationOption[] {
    return withCustomOption(this.specialtyOptions.slice(), this.specialtyVM.inputValue);
  }

  get isInstitutionLoading(): boolean {
    return (
      this.institutionSearchState.status === 'pending' &&
      this.activeInstitutionSearchRequestId !== null
    );
  }

  get isFacultiesLoading(): boolean {
    return this.facultiesLoadState.status === 'pending';
  }

  get isSpecialtyLoading(): boolean {
    return (
      this.specialtySearchState.status === 'pending' &&
      this.activeSpecialtySearchRequestId !== null
    );
  }

  onSearchInstitution(query: string): void {
    this.institution = query;
    this.institutionId = null;
    this.institutionVM.setInputValue(query);

    if (query.length < 2) {
      this.debouncedFetchInstitutions.cancel();
      this.institutionSearchRequestId += 1;
      this.activeInstitutionSearchRequestId = null;
      this.institutionOptions.clear();
      return;
    }

    this.debouncedFetchInstitutions(query);
  }

  onSelectInstitution(item: EducationOption): void {
    this.debouncedFetchInstitutions.cancel();
    this.institutionSearchRequestId += 1;
    this.activeInstitutionSearchRequestId = null;
    this.institution = item.name;
    this.institutionId = String(item.id).startsWith('custom:') ? null : item.id;
    this.institutionVM.select(item, item.name);
    this.faculty = '';
    this.specialty = '';
    this.facultyVM.clear();
    this.specialtyVM.clear();
    this.facultyOptions.clear();
    this.specialtyOptions.clear();

    if (this.showDetails && this.institutionId) {
      this.getFaculties(this.institutionId);
    }
  }

  onSearchFaculty(query: string): void {
    this.faculty = query;
    this.facultyVM.setInputValue(query);
  }

  onSelectFaculty(item: EducationOption): void {
    this.faculty = item.name;
    this.facultyVM.select(item, item.name);
  }

  onSearchSpecialty(query: string): void {
    this.specialty = query;
    this.specialtyVM.setInputValue(query);

    if (query.length < 2) {
      this.debouncedFetchSpecialties.cancel();
      this.specialtySearchRequestId += 1;
      this.activeSpecialtySearchRequestId = null;
      this.specialtyOptions.clear();
      return;
    }

    this.debouncedFetchSpecialties(query);
  }

  onSelectSpecialty(item: EducationOption): void {
    this.debouncedFetchSpecialties.cancel();
    this.specialtySearchRequestId += 1;
    this.activeSpecialtySearchRequestId = null;
    this.specialty = item.name;
    this.specialtyVM.select(item, item.name);
  }

  updateField<K extends keyof EducationFieldValues>(
    field: K,
    value: EducationFieldValues[K]
  ): void {
    switch (field) {
      case 'eduType':
        this.eduType = value as string;
        this.institution = '';
        this.institutionId = null;
        this.faculty = '';
        this.specialty = '';
        this.institutionVM.clear();
        this.facultyVM.clear();
        this.specialtyVM.clear();
        this.institutionOptions.clear();
        this.facultyOptions.clear();
        this.specialtyOptions.clear();
        break;
      case 'fromMonth':
        this.fromMonth = value as string;
        break;
      case 'fromYear':
        this.fromYear = value as string;
        break;
      case 'toMonth':
        this.toMonth = value as string;
        break;
      case 'toYear':
        this.toYear = value as string;
        break;
      case 'stillLearning':
        this.stillLearning = value as boolean;
        break;
      default:
        this[field] = value as never;
        break;
    }

    if (field === 'stillLearning' && value === true) {
      this.toMonth = '';
      this.toYear = '';
    }

    if ((field === 'toMonth' || field === 'toYear') && value) {
      this.stillLearning = false;
    }
  }

  setFacultyOptions(options: EducationOption[]): void {
    this.facultyOptions.replace(options);
  }

  dispose(): void {
    this.debouncedFetchInstitutions.cancel();
    this.debouncedFetchSpecialties.cancel();
  }

  private async getInstitutions(query: string): Promise<void> {
    const requestId = ++this.institutionSearchRequestId;
    this.activeInstitutionSearchRequestId = requestId;

    await this.institutionSearchState.run(
      () =>
        HttpClient.post<EducationSearchResponse>('/candidates/getInstitutions', {
          query,
          education_level: this.eduType,
        }),
      {
        onSuccess: (response) => {
          runInAction(() => {
            if (requestId === this.activeInstitutionSearchRequestId && response.status === 200) {
              this.institutionOptions.replace(response.response);
            }
          });
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activeInstitutionSearchRequestId) {
              this.institutionOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activeInstitutionSearchRequestId) {
              this.activeInstitutionSearchRequestId = null;
            }
          });
        },
      }
    );
  }

  private async getFaculties(institutionId: number | string): Promise<void> {
    await this.facultiesLoadState.run(
      () =>
        HttpClient.post<EducationSearchResponse>('/candidates/getFaculties', {
          institution_id: institutionId,
        }),
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            this.setFacultyOptions(response.response);
          }
        },
        onError: () => {
          this.setFacultyOptions([]);
        },
      }
    );
  }

  private async getSpecialties(query: string): Promise<void> {
    const requestId = ++this.specialtySearchRequestId;
    this.activeSpecialtySearchRequestId = requestId;

    await this.specialtySearchState.run(
      () =>
        HttpClient.post<EducationSearchResponse>('/candidates/getSpecialties', {
          query,
          institution_id: this.institutionId,
        }),
      {
        onSuccess: (response) => {
          runInAction(() => {
            if (requestId === this.activeSpecialtySearchRequestId && response.status === 200) {
              this.specialtyOptions.replace(response.response);
            }
          });
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activeSpecialtySearchRequestId) {
              this.specialtyOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activeSpecialtySearchRequestId) {
              this.activeSpecialtySearchRequestId = null;
            }
          });
        },
      }
    );
  }
}

export class AdditionalEducationItem {
  readonly id: string;
  courseName = '';
  institution = '';
  documentName = '';
  courseLink = '';
  certificate = '';
  fromMonth = '';
  fromYear = '';
  toMonth = '';
  toYear = '';
  stillLearning = false;

  constructor(initialValues: AdditionalEducationInitialValues = {}) {
    this.id = initialValues.id ?? uuidv4();
    this.courseName = initialValues.courseName ?? '';
    this.institution = initialValues.institution ?? '';
    this.documentName = initialValues.documentName ?? '';
    this.courseLink = initialValues.courseLink ?? '';
    this.certificate = normalizeCertificate(initialValues.certificate);
    this.fromMonth = initialValues.fromMonth ?? '';
    this.fromYear = initialValues.fromYear ?? '';
    this.toMonth = initialValues.toMonth ?? '';
    this.toYear = initialValues.toYear ?? '';
    this.stillLearning = initialValues.stillLearning ?? false;

    makeObservable(this, {
      courseName: observable,
      institution: observable,
      documentName: observable,
      courseLink: observable,
      certificate: observable,
      fromMonth: observable,
      fromYear: observable,
      toMonth: observable,
      toYear: observable,
      stillLearning: observable,
      hasValues: computed,
      hasDateValues: computed,
      valid: computed,
      updateField: action.bound,
    });
  }

  get hasValues(): boolean {
    return Boolean(
      this.courseName.trim() ||
        this.institution.trim() ||
        this.documentName.trim() ||
        this.courseLink.trim() ||
        this.certificate.trim() ||
        this.fromMonth.trim() ||
        this.fromYear.trim() ||
        this.toMonth.trim() ||
        this.toYear.trim()
    );
  }

  get hasDateValues(): boolean {
    return Boolean(
      this.fromMonth.trim() ||
        this.fromYear.trim() ||
        this.toMonth.trim() ||
        this.toYear.trim()
    );
  }

  get valid(): boolean {
    if (!this.hasDateValues) {
      return true;
    }

    const hasStartDateValues = Boolean(this.fromMonth.trim() || this.fromYear.trim());
    const hasStartDate =
      !hasStartDateValues || Boolean(this.fromMonth.trim() && this.fromYear.trim());
    const hasEndDate =
      this.stillLearning || Boolean(this.toMonth.trim() && this.toYear.trim());

    return Boolean(hasStartDate && hasEndDate);
  }

  updateField<K extends keyof AdditionalEducationFieldValues>(
    field: K,
    value: AdditionalEducationFieldValues[K]
  ): void {
    this[field] = value as never;

    if (field === 'stillLearning' && value === true) {
      this.toMonth = '';
      this.toYear = '';
    }

    if ((field === 'toMonth' || field === 'toYear') && value) {
      this.stillLearning = false;
    }
  }
}

const createEducation = (): EducationItem => new EducationItem();
const createAdditionalEducation = (): AdditionalEducationItem =>
  new AdditionalEducationItem();

export class EducationInfoStore {
  readonly educations = observable.array<EducationItem>([createEducation()]);
  readonly additionalEducations = observable.array<AdditionalEducationItem>([
    createAdditionalEducation(),
  ]);
  readonly years = observable.array<number>([]);
  readonly months = observable.array<EducationMonth>([]);
  readonly educationTypes = observable.array<EducationLevel>([]);

  constructor() {
    makeObservable(this, {
      educations: observable,
      additionalEducations: observable,
      years: observable,
      months: observable,
      educationTypes: observable,
      valid: computed,
      addEducation: action.bound,
      removeEducation: action.bound,
      addAdditionalEducation: action.bound,
      removeAdditionalEducation: action.bound,
      updateField: action.bound,
      updateAdditionalField: action.bound,
      update: action.bound,
      dispose: action.bound,
    });
  }

  get valid(): boolean {
    return (
      this.educations.every((item) => item.valid) &&
      this.additionalEducations.every((item) => item.valid)
    );
  }

  addEducation(): void {
    this.educations.push(createEducation());
  }

  removeEducation(id: string): void {
    if (this.educations.length === 1) {
      return;
    }

    const index = this.educations.findIndex((item) => item.id === id);
    if (index >= 0) {
      this.educations[index].dispose();
      this.educations.splice(index, 1);
    }
  }

  addAdditionalEducation(): void {
    this.additionalEducations.push(createAdditionalEducation());
  }

  removeAdditionalEducation(id: string): void {
    if (this.additionalEducations.length === 1) {
      return;
    }

    const index = this.additionalEducations.findIndex((item) => item.id === id);
    if (index >= 0) {
      this.additionalEducations.splice(index, 1);
    }
  }

  updateField<K extends keyof EducationFieldValues>(
    id: string,
    field: K,
    value: EducationFieldValues[K]
  ): void {
    const item = this.educations.find((education) => education.id === id);
    item?.updateField(field, value);
  }

  updateAdditionalField<K extends keyof AdditionalEducationFieldValues>(
    id: string,
    field: K,
    value: AdditionalEducationFieldValues[K]
  ): void {
    const item = this.additionalEducations.find((education) => education.id === id);
    item?.updateField(field, value);
  }

  update(payload: CandidateEditProfileEmpty): void {
    this.years.replace(payload.yearList);
    this.months.replace(payload.months);
    this.educationTypes.replace(payload.education_level);

    this.educations.forEach((education) => {
      education.dispose();
    });
    this.educations.clear();

    if (payload.userdetail.educations?.length) {
      payload.userdetail.educations.forEach((education) => {
        this.educations.push(
          new EducationItem({
            id: String(education.id ?? uuidv4()),
            eduType: String(education.edu_type ?? ''),
            institution: education.institution?.label ?? '',
            institutionId: education.institution?.value ?? null,
            faculty: education.faculty?.label ?? '',
            specialty: education.specialty?.label ?? '',
            fromMonth: String(education.from_month_edu ?? ''),
            fromYear: String(education.from_year_edu ?? ''),
            toMonth: String(education.to_month_edu ?? ''),
            toYear: String(education.to_year_edu ?? ''),
            stillLearning: Boolean(education.still_learning),
          })
        );
      });
    }

    if (!this.educations.length) {
      this.educations.push(createEducation());
    }

    this.additionalEducations.clear();

    if (payload.userdetail.additional_educations?.length) {
      payload.userdetail.additional_educations.forEach((education) => {
        this.additionalEducations.push(
          new AdditionalEducationItem({
            id: String(education.id ?? uuidv4()),
            courseName: education.course_name ?? '',
            institution: education.institution_add ?? '',
            documentName: education.diploma_type ?? '',
            courseLink: education.course_link ?? '',
            certificate: normalizeCertificate(education.certificate),
            fromMonth: String(education.from_month_edu_add ?? ''),
            fromYear: String(education.from_year_edu_add ?? ''),
            toMonth: String(education.to_month_edu_add ?? ''),
            toYear: String(education.to_year_edu_add ?? ''),
            stillLearning: Boolean(education.still_learning_add),
          })
        );
      });
    }

    if (!this.additionalEducations.length) {
      this.additionalEducations.push(createAdditionalEducation());
    }
  }

  dispose(): void {
    this.educations.forEach((education) => {
      education.dispose();
    });
  }
}
